import React, { useMemo, useState } from 'react';
import { Activity } from '../../types/Activity';

interface ActivityCardProps {
  activity: Activity & { day: number };
}

const UNSAFE_TAG_SELECTORS = 'script, style, iframe, object, embed, form, input, button';
const UNSAFE_ATTR_NAMES = new Set(['style', 'srcdoc']);
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizePlainTextHtml = (value: string) =>
  escapeHtml(value).replace(/\r\n|\r|\n/g, '<br />');

export const sanitizeActivityDescriptionHtml = (raw?: string): string => {
  const source = (raw || '').trim();
  if (!source) return '';

  if (!/<[^>]+>/.test(source)) {
    return normalizePlainTextHtml(source);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(source, 'text/html');
  const baseOrigin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://example.com';

  doc.querySelectorAll(UNSAFE_TAG_SELECTORS).forEach((node) => node.remove());

  doc.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const attrName = attribute.name.toLowerCase();
      if (attrName.startsWith('on') || UNSAFE_ATTR_NAMES.has(attrName)) {
        element.removeAttribute(attribute.name);
      }
    });

    if (element.tagName.toLowerCase() === 'a') {
      const href = element.getAttribute('href');
      if (href) {
        const isHashLink = href.startsWith('#');
        let isSafeProtocol = false;
        try {
          const parsed = new URL(href, baseOrigin);
          isSafeProtocol = SAFE_LINK_PROTOCOLS.has(parsed.protocol);
        } catch {
          isSafeProtocol = false;
        }
        if (!isHashLink && !isSafeProtocol) {
          element.removeAttribute('href');
        }
      }
      element.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return doc.body.innerHTML.trim();
};

const ActivityCard: React.FC<ActivityCardProps> = ({ activity }) => {
  const getActivityIcon = (category?: string) => {
    if (!category) return 'A';
    const categoryLower = category.toLowerCase();
    if (categoryLower.includes('sightseeing') || categoryLower.includes('tour')) return 'T';
    if (categoryLower.includes('adventure') || categoryLower.includes('sports')) return 'S';
    if (categoryLower.includes('cultural') || categoryLower.includes('museum')) return 'C';
    if (categoryLower.includes('nature') || categoryLower.includes('park')) return 'N';
    if (categoryLower.includes('water') || categoryLower.includes('beach')) return 'W';
    if (categoryLower.includes('food') || categoryLower.includes('dining')) return 'F';
    if (categoryLower.includes('shopping')) return 'P';
    if (categoryLower.includes('entertainment') || categoryLower.includes('show')) return 'E';
    if (categoryLower.includes('spa') || categoryLower.includes('wellness')) return 'L';
    return 'A';
  };

  const [isImageBroken, setIsImageBroken] = useState(false);
  const safeDescriptionHtml = useMemo(
    () => sanitizeActivityDescriptionHtml(activity.description),
    [activity.description]
  );

  return (
    <div className="pdf-activity-card">
      {activity.picture_url && !isImageBroken ? (
        <img
          className="pdf-activity-image"
          src={activity.picture_url}
          alt={activity.name}
          onError={() => setIsImageBroken(true)}
        />
      ) : (
        <div className="pdf-activity-image pdf-activity-image-fallback">
          {getActivityIcon(activity.category)}
        </div>
      )}
      <div className="pdf-activity-content">
          <div className="pdf-activity-name">{activity.name}</div>
        {safeDescriptionHtml && (
          <div
            className="pdf-activity-description"
            dangerouslySetInnerHTML={{ __html: safeDescriptionHtml }}
          />
        )}
      </div>
    </div>
  );
};

export default ActivityCard;
